import { useRef, useState, useEffect } from "react";
import api from "../lib/api";
import { BellPlus } from "lucide-react";
import { toast } from "sonner";

// Reusable "Remind me" button — attaches a personal timer to ANY record.
// props: entityType (lr|invoice|bid|ewb|""), entityId, label, defaultTitle, size
export function RemindMeButton({ entityType = "", entityId = "", label = "", defaultTitle = "", className = "", testId = "remind-me" }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle || (label ? `Follow up: ${label}` : "Reminder"));
  const [mode, setMode] = useState("recurring");
  const [value, setValue] = useState(2);
  const [unit, setUnit] = useState("hours");
  const [untilDone, setUntilDone] = useState(true);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  async function save() {
    if (!title.trim()) { toast.error("Add a reminder title"); return; }
    const v = Math.max(1, Number(value) || 1);
    const minutes = unit === "hours" ? v * 60 : v;
    const body = {
      title,
      ...(mode === "recurring" ? { interval_minutes: minutes } : { in_minutes: minutes }),
      link_type: entityType, link_id: entityId, link_label: label,
      auto_stop: !!entityId && untilDone,
    };
    setSaving(true);
    try {
      await api.post("/timers", body);
      window.dispatchEvent(new Event("timers-changed"));
      toast.success("Reminder set 🔔");
      setOpen(false);
    } catch { toast.error("Could not set reminder"); }
    finally { setSaving(false); }
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={className || "btn-ghost text-sm"} title="Set a reminder" data-testid={testId}>
        <BellPlus className="w-4 h-4" /> Remind me
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[290px] bg-white border border-[#E5E3DB] rounded-lg shadow-2xl p-3 space-y-2 text-left" data-testid={`${testId}-panel`}>
          <input className="input-field !py-1.5 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`${testId}-title`} />
          <div className="flex items-center gap-2">
            <select className="input-field !py-1.5 text-sm flex-1" value={mode} onChange={(e) => setMode(e.target.value)} data-testid={`${testId}-mode`}>
              <option value="recurring">Every</option>
              <option value="once">In</option>
            </select>
            <input type="number" min="1" className="input-field !py-1.5 text-sm w-16" value={value} onChange={(e) => setValue(e.target.value)} data-testid={`${testId}-value`} />
            <select className="input-field !py-1.5 text-sm" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid={`${testId}-unit`}>
              <option value="minutes">min</option>
              <option value="hours">hours</option>
            </select>
          </div>
          {entityId && mode === "recurring" && (
            <label className="flex items-center gap-2 text-xs text-[#4A4842] cursor-pointer">
              <input type="checkbox" checked={untilDone} onChange={(e) => setUntilDone(e.target.checked)} data-testid={`${testId}-until-done`} />
              Stop automatically once {label || "this"} is done
            </label>
          )}
          <button onClick={save} disabled={saving} className="btn-primary w-full !py-1.5 text-sm" data-testid={`${testId}-save`}>{saving ? "Setting…" : "Set reminder"}</button>
        </div>
      )}
    </div>
  );
}

export default RemindMeButton;
